import { useState, useEffect } from 'react';
import { useUserData } from '../hooks/useUserData';
import Logo from '../components/Logo';
import { BanknotesIcon, CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  Bars3Icon, 
  XMarkIcon,
  HomeIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  CreditCardIcon,
  UserCircleIcon,
  CogIcon,
  CurrencyEuroIcon,
  ChartPieIcon,
  BuildingOfficeIcon,
  CircleStackIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  ArrowUpCircleIcon,
  ArrowDownCircleIcon,
  QrCodeIcon,
  WalletIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ArrowLeftIcon,
  UsersIcon,
  PhoneIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { usePaymentSettings } from '../firebase/paymentSettings';
import { QRCodeSVG } from 'qrcode.react';
import CryptoMarketData from '../components/CryptoMarketData';
import PortfolioPerformance from '../components/PortfolioPerformance';
import CryptoWidgets from '../components/CryptoWidgets';
import CryptoPriceTicker from '../components/CryptoPriceTicker';
import TradingViewWidget from '../components/TradingViewWidget';
import CryptoPriceWidget from '../components/CryptoPriceWidget';
import { useAuth } from '../contexts/AuthContext';
import OTPVerification from '../components/OTPVerification';
import { addDoc, collection, doc, updateDoc, increment, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

// Add this helper function at the top of the file, outside the component
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInYears > 0) {
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''}`
  } else if (diffInMonths > 0) {
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''}`
  } else {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''}`
  }
};

function UserDashboard() {
  const { userData, loading, error } = useUserData();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [notifications, setNotifications] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('crypto');
  const [withdrawMethod, setWithdrawMethod] = useState('crypto');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const { currentUser, isAdmin } = useAuth();
  const [depositMethods, setDepositMethods] = useState({
    crypto: {
      title: "Cryptocurrency",
      description: "Fast and secure crypto deposits",
      methods: []
    },
    cashapp: {
      title: "CashApp",
      description: "Quick and easy mobile payments",
      cashtag: "",
      instructions: []
    },
    paypal: {
      title: "PayPal",
      description: "Safe and instant transfers",
      email: "",
      instructions: []
    },
    bank: {
      title: "Bank Transfer",
      description: "Traditional bank wire transfer",
      details: {}
    }
  });
  const [showInvestmentsModal, setShowInvestmentsModal] = useState(false);
  const [showStatCards, setShowStatCards] = useState(false); // Collapsible state for original stat cards - collapsed by default
  const [bankDetails, setBankDetails] = useState({
    'Bank Name': '',
    'Account Number': '',
    'Routing Number': '',
    'Account Holder Name': '',
    'Bank Address': ''
  });
  const [withdrawalActivities, setWithdrawalActivities] = useState([]);

  // Shared balance calculation function
  const calculateTotalBalance = () => {
    if (!userData?.investments || userData.investments.length === 0) return 0;
    
    let totalBalance = 0;
    
    userData.investments.forEach(investment => {
      if (investment.status === 'active') {
        // Calculate daily returns for active investments
        const startDate = new Date(investment.startDate);
        const now = new Date();
        const elapsedDays = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
        const totalDays = 21;
        
        if (elapsedDays >= 0 && elapsedDays < totalDays) {
          // Investment is still active, calculate accumulated returns
          const investmentAmount = parseFloat(investment.amount || 0);
          const multiplier = parseFloat(investment.multiplier || 10); // Default to 10x
          const totalReturn = investmentAmount * multiplier;
          const dailyReturn = totalReturn / totalDays;
          const currentReturns = dailyReturn * elapsedDays;
          
          totalBalance += currentReturns;
        } else if (elapsedDays >= totalDays) {
          // Investment is complete, add full returns
          const investmentAmount = parseFloat(investment.amount || 0);
          const multiplier = parseFloat(investment.multiplier || 10);
          totalBalance += investmentAmount * multiplier;
        }
      } else {
        // For completed investments, add the balance
        totalBalance += parseFloat(investment.balance || 0);
      }
    });
    
    return totalBalance;
  };

  // Generate recent activity data from user investments and other data
  const generateRecentActivity = () => {
    const activities = [];
    
    // Add investment activities
    if (userData?.investments && userData.investments.length > 0) {
      userData.investments.forEach(investment => {
        // Investment start activity
        activities.push({
          id: `investment-start-${investment.id || Date.now()}`,
          type: 'investment',
          title: `Started ${investment.planName}`,
          description: `Investment plan activated`,
          amount: parseFloat(investment.amount || 0),
          timestamp: new Date(investment.startDate),
          status: 'completed',
          icon: 'investment'
        });

        // Daily returns for active investments
        if (investment.status === 'active') {
          const startDate = new Date(investment.startDate);
          const now = new Date();
          const elapsedDays = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
          const totalDays = 21;
          
          if (elapsedDays > 0 && elapsedDays < totalDays) {
            const investmentAmount = parseFloat(investment.amount || 0);
            const multiplier = parseFloat(investment.multiplier || 10);
            const totalReturn = investmentAmount * multiplier;
            const dailyReturn = totalReturn / totalDays;
            const currentReturns = dailyReturn * elapsedDays;
            
            activities.push({
              id: `daily-returns-${investment.id || Date.now()}`,
              type: 'returns',
              title: `Daily Returns - ${investment.planName}`,
              description: `Accumulated returns from active investment`,
              amount: currentReturns,
              timestamp: new Date(),
              status: 'active',
              icon: 'returns'
            });
          }
        }

        // Investment completion activity
        if (investment.status === 'completed') {
          activities.push({
            id: `investment-complete-${investment.id || Date.now()}`,
            type: 'completion',
            title: `Completed ${investment.planName}`,
            description: `Investment plan completed successfully`,
            amount: parseFloat(investment.balance || investment.expectedReturn || 0),
            timestamp: new Date(investment.endDate || investment.startDate),
            status: 'completed',
            icon: 'completion'
          });
        }
      });
    }

    // Add withdrawal approval/rejection activities
    withdrawalActivities.forEach(a => {
      activities.push({
        id: `withdrawal-${a.id}`,
        type: 'withdrawal',
        title: a.status === 'approved' ? 'Withdrawal Approved' : 'Withdrawal Rejected',
        description: a.message,
        amount: a.amount,
        timestamp: new Date(a.processedAt || a.timestamp),
        status: a.status,
        icon: a.status === 'approved' ? 'check' : 'close',
      });
    });

    // Add withdrawal activities (if any pending withdrawals)
    if (userData?.accountDetails?.pendingWithdrawals && userData.accountDetails.pendingWithdrawals > 0) {
      activities.push({
        id: 'pending-withdrawal',
        type: 'withdrawal',
        title: 'Withdrawal Request',
        description: 'Pending withdrawal request',
        amount: parseFloat(userData.accountDetails.pendingWithdrawals),
        timestamp: new Date(),
        status: 'pending',
        icon: 'withdrawal'
      });
    }

    // Add account creation activity
    if (userData?.createdAt) {
      activities.push({
        id: 'account-creation',
        type: 'account',
        title: 'Account Created',
        description: 'Welcome to Fortune Oak',
        amount: 0,
        timestamp: new Date(userData.createdAt),
        status: 'completed',
        icon: 'account'
      });
    }

    // Sort by timestamp (most recent first) and limit to 10 items
    return activities
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 10);
  };

  const cryptoData = [
    { name: 'BTC', value: 45000, change: '+5.2%', symbol: 'Bitcoin' },
    { name: 'ETH', value: 3200, change: '+3.8%', symbol: 'Ethereum' },
    { name: 'SOL', value: 120, change: '-2.1%', symbol: 'Solana' },
    { name: 'ADA', value: 0.45, change: '+1.2%', symbol: 'Cardano' },
    { name: 'DOT', value: 7.2, change: '+4.5%', symbol: 'Polkadot' },
    { name: 'LINK', value: 15.8, change: '-0.8%', symbol: 'Chainlink' },
    { name: 'MATIC', value: 0.85, change: '+2.3%', symbol: 'Polygon' },
    { name: 'AVAX', value: 35.6, change: '+6.7%', symbol: 'Avalanche' },
    { name: 'UNI', value: 8.9, change: '+1.9%', symbol: 'Uniswap' },
    { name: 'ATOM', value: 12.3, change: '-1.4%', symbol: 'Cosmos' }
  ];

  const portfolioData = [
    { name: 'Stocks', value: 40 },
    { name: 'Crypto', value: 30 },
    { name: 'Real Estate', value: 20 },
    { name: 'Cash', value: 10 },
  ];

  const COLORS = ['#4F46E5', '#06B6D4', '#10B981', '#F59E0B'];

  const performanceData = [
    { month: '2024-01-01', value: 2000, profit: 1200 },
    { month: '2024-02-01', value: 3500, profit: 2100 },
    { month: '2024-03-01', value: 5000, profit: 3000 },
    { month: '2024-04-01', value: 7000, profit: 4200 },
    { month: '2024-05-01', value: 9500, profit: 5700 },
    { month: '2024-06-01', value: 12000, profit: 7200 }
  ];

  const PORTFOLIO_ITEMS = [
    { 
      name: 'Stocks', 
      key: 'stocks',
      icon: ArrowTrendingUpIcon, 
      color: '#10B981' // Green
    },
    { 
      name: 'Crypto', 
      key: 'crypto',
      icon: CircleStackIcon, 
      color: '#F59E0B' // Amber
    },
    { 
      name: 'Real Estate', 
      key: 'realEstate',
      icon: BuildingOfficeIcon, 
      color: '#3B82F6' // Blue
    },
    { 
      name: 'Cash', 
      key: 'cash',
      icon: CurrencyDollarIcon, 
      color: '#EC4899' // Pink
    }
  ];

  const withdrawMethods = {
    crypto: {
      title: "Cryptocurrency",
      description: "Fast crypto withdrawals",
      options: [
        { name: "Bitcoin (BTC)", fee: "0.0005 BTC" },
        { name: "Ethereum (ETH)", fee: "0.01 ETH" },
        { name: "USDT (TRC20)", fee: "1 USDT" }
      ]
    },
    bank: {
      title: "Bank Transfer",
      description: "2-5 business days",
      fee: "$25 wire fee",
      requirements: [
        "Bank Name",
        "Account Number",
        "Routing Number",
        "Account Holder Name",
        "Bank Address"
      ]
    },
    cashapp: {
      title: "CashApp",
      description: "Instant withdrawals",
      fee: "1.5%",
      limit: "$7,500 per week"
    },
    paypal: {
      title: "PayPal",
      description: "Instant withdrawals",
      fee: "2.9% + $0.30",
      limit: "$10,000 per transaction"
    }
  };

  const paymentSettings = usePaymentSettings();

  useEffect(() => {
    if (paymentSettings) {
      setDepositMethods({
        crypto: {
          title: "Cryptocurrency",
          description: "Fast and secure crypto deposits",
          methods: [
            {
              name: paymentSettings.crypto.bitcoin.name,
              address: paymentSettings.crypto.bitcoin.address
            },
            {
              name: paymentSettings.crypto.ethereum.name,
              address: paymentSettings.crypto.ethereum.address
            },
            {
              name: paymentSettings.crypto.usdt.name,
              address: paymentSettings.crypto.usdt.address
            }
          ]
        },
        cashapp: {
          title: "CashApp",
          description: "Quick and easy mobile payments",
          cashtag: paymentSettings.cashapp.cashtag,
          instructions: paymentSettings.cashapp.instructions
        },
        paypal: {
          title: "PayPal",
          description: "Safe and instant transfers",
          email: paymentSettings.paypal.email,
          instructions: paymentSettings.paypal.instructions
        },
        bank: {
          title: "Bank Transfer",
          description: "Traditional bank wire transfer",
          details: paymentSettings.bank.details
        }
      });
    }
  }, [paymentSettings]);

  // Add access control effect
  useEffect(() => {
    if (isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [isAdmin, navigate]);

  // Add effect to handle URL query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab && ['dashboard', 'deposit', 'withdraw', 'notifications'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Listen for withdrawal status changes for this user
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'withdrawalRequests'),
      where('userId', '==', currentUser.uid)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const activities = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data.status === 'approved' || data.status === 'rejected') {
          activities.push({
            id: docSnap.id,
            type: 'withdrawal',
            status: data.status,
            amount: data.amount || data.withdrawalDetails?.amount,
            method: data.method || data.withdrawalDetails?.method,
            processedAt: data.processedAt,
            timestamp: data.timestamp,
            message: data.status === 'approved'
              ? `Your withdrawal of $${(data.amount || data.withdrawalDetails?.amount)?.toLocaleString()} was approved.`
              : `Your withdrawal of $${(data.amount || data.withdrawalDetails?.amount)?.toLocaleString()} was rejected.`,
          });
        }
      });
      setWithdrawalActivities(activities.sort((a, b) => new Date(b.processedAt || b.timestamp) - new Date(a.processedAt || a.timestamp)));
    });
    return () => unsubscribe();
  }, [currentUser]);

  // Merge withdrawal activities into notifications and recent activity
  useEffect(() => {
    if (withdrawalActivities.length === 0) return;
    // Add to notifications
    setNotifications(prev => {
      // Avoid duplicates
      const ids = new Set(prev.map(n => n.id));
      const newNotes = withdrawalActivities.filter(a => !ids.has(a.id)).map(a => ({
        id: a.id,
        title: a.status === 'approved' ? 'Withdrawal Approved' : 'Withdrawal Rejected',
        message: a.message,
        timestamp: a.processedAt || a.timestamp,
        read: false,
      }));
      return [...newNotes, ...prev];
    });
  }, [withdrawalActivities]);

  const NotificationsPanel = () => (
    <div className="p-6">
      <BackButton />
      <div className="card-glass bg-gradient-to-br from-slate-800/90 to-slate-900/90 rounded-2xl shadow-xl overflow-hidden border border-slate-700">
        <div className="border-b border-slate-700">
          <div className="p-4 bg-gradient-to-r from-blue-900/60 to-purple-900/60">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Notifications</h2>
          </div>
        </div>
        <div className="divide-y divide-slate-700/60">
          {notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 ${notification.read ? 'bg-slate-900/60' : 'bg-blue-900/40'} text-white`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <BellIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <p className="text-sm font-medium text-blue-200">
                    {notification.title}
                  </p>
                  <div className="mt-1 text-sm text-blue-300 whitespace-pre-line">
                    {notification.message}
                  </div>
                  <div className="mt-2 text-xs text-blue-400">
                    {new Date(notification.timestamp).toLocaleDateString()} at{' '}
                    {new Date(notification.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          {notifications.length === 0 && (
            <div className="p-4 text-center text-blue-300">
              No notifications yet
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Mobile Navigation (replaces TopNav)
  const MobileNav = () => {
    return (
    <nav className="lg:hidden bg-slate-900 border-b border-slate-700 shadow-xl fixed w-full top-0 z-50">
      <div className="px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <button
              onClick={() => window.open('/', '_blank')}
              className="hover:opacity-80 transition-opacity duration-200"
              title="Go to Home"
            >
              <Logo darkMode={false} />
            </button>
          </div>
          
          {/* User Profile & Menu Button */}
          <div className="flex items-center space-x-3">
            {/* Quick User Info */}
            <div className="hidden sm:flex items-center space-x-2 text-right">
              <div>
                <p className="text-xs text-blue-300 font-medium truncate max-w-20">
                  {userData?.fullName || userData?.name || 'Investor'}
                </p>
                <p className="text-xs text-white/70">
                  ${calculateTotalBalance().toLocaleString()}
                </p>
              </div>
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                <UserCircleIcon className="h-4 w-4 text-white" />
              </div>
            </div>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-lg text-white hover:text-blue-300 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Mobile Menu */}
      <div className={`${mobileMenuOpen ? 'block' : 'hidden'} absolute w-full bg-slate-900 border-b border-slate-700 shadow-2xl max-h-[80vh] overflow-y-auto`}>
        <div className="pt-4 pb-6">
          {/* User Profile Section */}
          <div className="px-4 py-4 border-b border-slate-700 mb-4 bg-slate-800/50">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <UserCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">
                  {userData?.fullName || userData?.name || 'Investor'}
                </p>
                <p className="text-xs text-blue-300 truncate">
                  ${calculateTotalBalance().toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="px-4 mb-6">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  setActiveTab('deposit');
                  setMobileMenuOpen(false);
                }}
                className="flex items-center justify-center space-x-2 p-3 rounded-xl bg-green-600/20 border border-green-500/50 text-green-300 hover:bg-green-600/30 transition-all duration-200"
              >
                <ArrowUpCircleIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Deposit</span>
              </button>
              <button
                onClick={() => {
                  setActiveTab('withdraw');
                  setMobileMenuOpen(false);
                }}
                className="flex items-center justify-center space-x-2 p-3 rounded-xl bg-blue-600/20 border border-blue-500/50 text-blue-300 hover:bg-blue-600/30 transition-all duration-200"
              >
                <ArrowDownCircleIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Withdraw</span>
              </button>
            </div>
          </div>

          {/* Main Navigation */}
          <div className="space-y-1">
            {/* Dashboard Section */}
            <div className="px-4 py-2">
              <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-3">Dashboard</h3>
            </div>
            <MobileNavLink 
              icon={<HomeIcon className="w-5 h-5" />} 
              text="Dashboard" 
              active={activeTab === 'dashboard'}
              onClick={() => {
                setActiveTab('dashboard');
                setMobileMenuOpen(false);
              }}
            />
            <MobileNavLink 
              icon={<BellIcon className="w-5 h-5" />} 
              text="Notifications" 
              active={activeTab === 'notifications'}
              badge={notifications.filter(n => !n.read).length}
              onClick={() => {
                setActiveTab('notifications');
                setMobileMenuOpen(false);
              }}
            />

            {/* External Navigation Section */}
            <div className="px-4 py-2 mt-6">
              <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-3">Navigation</h3>
            </div>
            <MobileNavLink 
              icon={<HomeIcon className="w-5 h-5" />} 
              text="Home" 
              onClick={() => {
                window.open('/', '_blank');
                setMobileMenuOpen(false);
              }}
            />
            <MobileNavLink 
              icon={<UsersIcon className="w-5 h-5" />} 
              text="About" 
              onClick={() => {
                window.open('/about', '_blank');
                setMobileMenuOpen(false);
              }}
            />
            <MobileNavLink 
              icon={<ChartBarIcon className="w-5 h-5" />} 
              text="Investment Plans" 
              onClick={() => {
                window.open('/investment-plans', '_blank');
                setMobileMenuOpen(false);
              }}
            />
            <MobileNavLink 
              icon={<BuildingOfficeIcon className="w-5 h-5" />} 
              text="Portfolio" 
              onClick={() => {
                window.open('/portfolio', '_blank');
                setMobileMenuOpen(false);
              }}
            />
            <MobileNavLink 
              icon={<PhoneIcon className="w-5 h-5" />} 
              text="Contact" 
              onClick={() => {
                window.open('/contact', '_blank');
                setMobileMenuOpen(false);
              }}
            />

            {/* Sign Out Section */}
            <div className="px-4 py-2 mt-6">
              <MobileNavLink 
                icon={<ArrowRightOnRectangleIcon className="w-5 h-5" />} 
                text="Sign Out" 
                onClick={() => {
                  handleSignOut();
                  setMobileMenuOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      // Optionally add error notification here
    }
  };

  const Sidebar = () => (
    <div className="hidden lg:flex fixed inset-y-0 left-0 w-64 card-glass bg-gradient-to-b from-slate-900/90 to-slate-800/90 border-r border-slate-700 shadow-xl z-30">
      <div className="flex flex-col w-full h-full">
        {/* Logo Section - Fixed at top */}
        <div className="flex-shrink-0 p-4 border-b border-slate-700/60 bg-slate-900/50">
          <div className="relative z-10">
            <button
              onClick={() => window.open('/', '_blank')}
              className="hover:opacity-80 transition-opacity duration-200"
              title="Go to Home"
            >
              <Logo darkMode={true} />
            </button>
          </div>
        </div>

        {/* User Profile Section - Fixed below logo */}
        <div className="flex-shrink-0 p-4 border-b border-slate-700/60 bg-slate-800/30">
          <motion.div 
            className="flex items-center space-x-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <UserCircleIcon className="h-6 w-6 text-white" />
            </motion.div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {userData?.fullName || userData?.name || 'Investor'}
              </p>
              <p className="text-xs text-blue-300 truncate">
                ${calculateTotalBalance().toLocaleString()}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Scrollable Navigation */}
        <div className="flex-1 overflow-y-auto scrollbar-sidebar">
          <nav className="p-4 space-y-3">
            {/* Dashboard Section */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-3 px-1">Dashboard</h3>
              <div className="space-y-2">
          <SidebarLink 
                  icon={<HomeIcon className="w-5 h-5" />} 
            text="Dashboard" 
            active={activeTab === 'dashboard'}
            onClick={() => setActiveTab('dashboard')}
          />
          <SidebarLink 
            icon={<ArrowUpCircleIcon className="w-5 h-5" />} 
            text="Deposit" 
            active={activeTab === 'deposit'}
            onClick={() => setActiveTab('deposit')}
          />
          <SidebarLink 
            icon={<ArrowDownCircleIcon className="w-5 h-5" />} 
            text="Withdraw" 
            active={activeTab === 'withdraw'}
            onClick={() => setActiveTab('withdraw')}
          />
          <SidebarLink 
            icon={<BellIcon className="w-5 h-5" />} 
            text="Notifications" 
            active={activeTab === 'notifications'}
            onClick={() => setActiveTab('notifications')}
            badge={notifications.filter(n => !n.read).length.toString()}
          />
              </div>
            </div>

            {/* Navigation Section */}
            <div className="border-t border-slate-700/60 pt-6">
              <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-3 px-1">Navigation</h3>
              <div className="space-y-2">
                <SidebarLink 
                  icon={<HomeIcon className="w-5 h-5" />} 
                  text="Home" 
                  isExternal={true}
                  onClick={() => window.open('/', '_blank')}
                />
                <SidebarLink 
                  icon={<UsersIcon className="w-5 h-5" />} 
                  text="About" 
                  isExternal={true}
                  onClick={() => window.open('/about', '_blank')}
                />
                <SidebarLink 
                  icon={<ChartBarIcon className="w-5 h-5" />} 
                  text="Plans" 
                  isExternal={true}
                  onClick={() => window.open('/investment-plans', '_blank')}
                  title="Investment Plans"
                />
                <SidebarLink 
                  icon={<BuildingOfficeIcon className="w-5 h-5" />} 
                  text="Portfolio" 
                  isExternal={true}
                  onClick={() => window.open('/portfolio', '_blank')}
                />
                <SidebarLink 
                  icon={<PhoneIcon className="w-5 h-5" />} 
                  text="Contact" 
                  isExternal={true}
                  onClick={() => window.open('/contact', '_blank')}
                />
              </div>
            </div>
        </nav>
        </div>

        {/* Sign Out Section - Fixed at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-slate-700/60 bg-slate-800/30">
          <motion.button
            onClick={handleSignOut}
            className="flex items-center space-x-3 p-3 w-full rounded-xl text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200 group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 group-hover:rotate-12 transition-transform duration-200" />
            <span className="font-medium">Sign Out</span>
          </motion.button>
        </div>
      </div>
    </div>
  );

  // Back Button Component
  const BackButton = () => (
    <motion.button
      onClick={() => setActiveTab('dashboard')}
      className="mb-6 inline-flex items-center px-3 py-2 text-sm font-medium text-blue-300 hover:text-blue-400 transition-colors"
      whileHover={{ x: -4 }}
    >
      <ArrowLeftIcon className="w-5 h-5 mr-2" />
      Back to Dashboard
    </motion.button>
  );

  const SidebarLink = ({ icon, text, active, badge, isExternal, onClick, title }) => (
    <motion.button
      onClick={onClick}
      title={title}
      className={`flex items-center justify-between p-3 rounded-xl transition-all duration-200 w-full group ${
        active 
          ? 'bg-gradient-to-r from-blue-700/80 to-purple-700/80 text-white shadow-lg border border-blue-500/30' 
          : 'text-white/80 hover:bg-white/10 hover:text-white hover:shadow-md border border-transparent hover:border-white/10'
      }`}
      whileHover={{ scale: 1.02, x: 2 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-center space-x-3">
        <div className={`transition-transform duration-200 ${active ? 'scale-110' : 'group-hover:scale-110'}`}>
        {icon}
        </div>
        <span className="font-medium">{text}</span>
        {isExternal && (
          <svg className="w-3 h-3 text-blue-400 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        )}
      </div>
      {badge && (
        <motion.span 
          className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        >
          {badge}
        </motion.span>
      )}
    </motion.button>
  );

  const MobileNavLink = ({ icon, text, active, badge, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center justify-between w-full px-4 py-4 text-base font-medium rounded-lg mx-2 transition-all duration-200 ${
        active
          ? 'bg-blue-600 text-white border border-blue-500 shadow-lg'
          : 'text-white hover:text-blue-300 hover:bg-slate-800 border border-transparent hover:border-slate-600'
      }`}
    >
      <div className="flex items-center space-x-4">
        <div className={`p-2 rounded-lg ${
          active 
            ? 'bg-blue-700 text-white' 
            : 'bg-slate-700 text-white/80'
        }`}>
          {icon}
        </div>
        <span className="font-medium">{text}</span>
      </div>
      {badge && (
        <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-full min-w-[20px] text-center">
          {badge}
        </span>
      )}
    </button>
  );

  const getPortfolioData = () => {
    if (!userData?.portfolio) return [];
    
    return PORTFOLIO_ITEMS.map(item => ({
      name: item.name,
      value: userData.portfolio[item.key] || 0,
      color: item.color,
      icon: item.icon
    }));
  };

  useEffect(() => {
    if (userData?.name) {
      setNotifications([
        {
          id: 'welcome',
          type: 'welcome',
          title: `Welcome ${userData.name}!`,
          message: `Welcome to Fortune Oak Wealth! Here's how to get started:\n\n1. Make your first deposit by clicking the 'Deposit' tab\n2. Choose from our investment plans to start growing your portfolio\n3. Track your investments in real-time from your dashboard\n4. Withdraw your profits easily when ready\n\nFor any questions or support, please email support@fortuneoakwealth.com`,
          timestamp: userData.createdAt || new Date(),
          read: false
        }
      ]);
    }
  }, [userData?.name]);

  // Function to add withdrawal notification
  const addWithdrawalNotification = (withdrawalDetails) => {
    setNotifications(prev => [
      {
        id: `withdrawal-${Date.now()}`,
        type: 'withdrawal',
        title: 'Withdrawal Request Submitted',
        message: `Your withdrawal request for $${withdrawalDetails.amount.toLocaleString()} via ${withdrawalDetails.method} has been submitted and is pending approval. You will be notified once it's processed.`,
        timestamp: new Date(),
        read: false
      },
      ...prev
    ]);
  };

  const DepositPanel = () => {
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [selectedCrypto, setSelectedCrypto] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(() => {
      const savedPlan = localStorage.getItem('selectedPlan');
      return savedPlan ? JSON.parse(savedPlan) : null;
    });

    const plans = [
      {
        name: "Standard Plan",
        price: "$1,000",
        features: [
          "10x Profit in 21 days",
          "2% Welcome bonus",
          "$500 earn $5,000",
          "$2,000 earn $20,000",
          "$3,000 earn $30,000",
          "$4,000 earn $40,000",
          "$5,000 earn $50,000"
        ],
        duration: "21 days",
        type: "Basic portfolio",
        support: "24/7 Email Support"
      },
      {
        name: "Premium Plan",
        price: "$6,000",
        features: [
          "15x Profit in 21 days",
          "3% Welcome bonus",
          "$6,000 earn $90,000",
          "$7,000 earn $105,000",
          "$8,000 earn $120,000",
          "$9,000 earn $135,000",
          "$10,000 earn $150,000"
        ],
        duration: "21 days",
        type: "Enhanced portfolio",
        support: "24/7 Priority Chat Support",
        popular: true
      },
      {
        name: "Advanced Plan",
        price: "$10,000",
        features: [
          "20x Profit in 21 days",
          "5% Welcome bonus",
          "$10,000 earn $200,000",
          "$11,000 earn $220,000",
          "$12,000 earn $240,000",
          "$13,000 earn $260,000",
          "$14,000 earn $280,000",
          "VIP Account Manager",
          "Priority Withdrawals",
          "Exclusive Market Analysis"
        ],
        duration: "21 days",
        type: "VIP Wealth Management",
        support: "Dedicated Account Manager"
      },
      {
        name: "Platinum Plan",
        price: "$25,000",
        features: [
          "25x Profit in 21 days",
          "7% Welcome bonus",
          "$25,000 earn $625,000",
          "$30,000 earn $750,000",
          "$35,000 earn $875,000",
          "$40,000 earn $1,000,000",
          "$45,000 earn $1,125,000",
          "Elite Account Manager",
          "Instant Withdrawals",
          "Advanced Market Analysis",
          "Private Investment Events",
          "Personalized Portfolio Strategy"
        ],
        duration: "21 days",
        type: "Elite Wealth Management",
        support: "24/7 Personal Concierge"
      },
      {
        name: "Diamond Plan",
        price: "$50,000",
        features: [
          "30x Profit in 21 days",
          "10% Welcome bonus",
          "$50,000 earn $1,500,000",
          "$60,000 earn $1,800,000",
          "$70,000 earn $2,100,000",
          "$80,000 earn $2,400,000",
          "$90,000 earn $2,700,000",
          "Executive Account Manager",
          "Instant Priority Withdrawals",
          "Expert Market Analysis",
          "Exclusive Investment Events",
          "Custom Portfolio Strategy",
          "Global Investment Opportunities",
          "Wealth Preservation Consulting"
        ],
        duration: "21 days",
        type: "Executive Wealth Management",
        support: "Dedicated Executive Team"
      }
    ];

    const handlePlanChange = (plan) => {
      setSelectedPlan(plan);
      localStorage.setItem('selectedPlan', JSON.stringify(plan));
    };

    const CryptoView = ({ crypto, onBack }) => (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="inline-flex items-center text-sm font-medium text-blue-300 hover:text-blue-400 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Payment Methods
          </button>
          <span className="text-sm font-medium text-blue-200">{crypto.name}</span>
        </div>

        <div className="card-glass bg-gradient-to-br from-slate-900/90 to-slate-800/90 p-6 rounded-2xl shadow-xl space-y-6 border border-slate-700">
          {/* QR Code Section */}
          <div className="flex flex-col items-center space-y-4">
            <div className="w-48 h-48 card-glass bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-3 border border-slate-700">
              <QRCodeSVG
                value={crypto.address}
                size={176}
                level="H"
                includeMargin={true}
                className="w-full h-full"
              />
            </div>
            <h3 className="text-lg font-semibold text-white">{crypto.name} Deposit</h3>
          </div>

          {/* Address Section */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-blue-200">Deposit Address</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={crypto.address}
                readOnly
                className="flex-1 p-3 text-sm card-glass bg-gradient-to-r from-slate-900/60 to-slate-800/60 rounded-lg border border-slate-700 font-mono break-all text-white"
              />
              <button
                onClick={() => navigator.clipboard.writeText(crypto.address)}
                className="w-full sm:w-auto px-4 py-2 text-blue-300 bg-blue-900/40 hover:bg-blue-900/60 rounded-lg font-medium transition-colors inline-flex items-center justify-center border border-blue-700"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-4M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy Address
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-blue-200">Instructions</h4>
            <div className="card-glass bg-gradient-to-br from-blue-900/60 to-blue-800/60 rounded-xl p-4 border border-blue-700">
              <ul className="list-decimal list-inside space-y-2 text-sm text-blue-200">
                <li>Send only {crypto.name} to this deposit address</li>
                <li>Ensure the network is correct to avoid loss of funds</li>
                <li>Your deposit will be credited after network confirmation</li>
                <li>Send proof of payment to support@fortuneoakwealth.com</li>
              </ul>
            </div>
          </div>

          {/* Network Information */}
          <div className="card-glass bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-slate-700">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-white">Network Information</h4>
                <p className="mt-1 text-sm text-blue-200">
                  Network: {crypto.name === 'Bitcoin (BTC)' ? 'Bitcoin Network' : 
                           crypto.name === 'Ethereum (ETH)' ? 'ERC20' : 
                           'TRC20'}
                </p>
                <p className="mt-1 text-sm text-blue-200">
                  Processing Time: {crypto.name === 'Bitcoin (BTC)' ? '1 hour' : '15 minutes'} after confirmation
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <div className="p-6">
        <BackButton />
        <div className="card-glass bg-gradient-to-br from-slate-800/90 to-slate-900/90 rounded-2xl shadow-xl overflow-hidden border border-slate-700">
          <div className="border-b border-slate-700">
            <div className="p-4 bg-gradient-to-r from-blue-900/60 to-purple-900/60">
              <h2 className="text-lg font-semibold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Deposit Funds</h2>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Selected Plan Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-blue-200">
                Selected Investment Plan
              </label>
              <select
                value={selectedPlan?.name || ''}
                onChange={(e) => {
                  const plan = plans.find(p => p.name === e.target.value);
                  handlePlanChange(plan);
                }}
                className="w-full p-3 card-glass bg-gradient-to-r from-slate-900/60 to-slate-800/60 rounded-xl border border-slate-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="" className="bg-slate-800">Select a plan</option>
                {plans.map((plan, index) => (
                  <option key={index} value={plan.name} className="bg-slate-800">
                    {plan.name} - {plan.price}
                  </option>
                ))}
              </select>
              {selectedPlan && (
                <div className="mt-3 p-4 card-glass bg-gradient-to-br from-blue-900/40 to-purple-900/40 rounded-xl border border-blue-700/50">
                  <p className="text-sm text-blue-200">
                    <span className="font-semibold text-white">Selected Plan:</span> {selectedPlan.name}
                  </p>
                  <p className="text-sm text-blue-200">
                    <span className="font-semibold text-white">Minimum Amount:</span> {selectedPlan.price}
                  </p>
                </div>
              )}
            </div>

            {/* Important Notice */}
            <div className="card-glass bg-gradient-to-br from-amber-900/60 to-orange-900/60 border-l-4 border-amber-500 rounded-xl p-3 sm:p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-amber-400" aria-hidden="true" />
                </div>
                <div className="ml-2 sm:ml-3">
                  <h3 className="text-base sm:text-lg font-semibold text-amber-200">Important Notice:</h3>
                  <ul className="mt-2 list-disc list-inside space-y-1 text-amber-200">
                    <li className="font-medium text-xs sm:text-sm leading-relaxed">Deposits must be completed within 24 hours of initiation</li>
                    <li className="font-medium text-xs sm:text-sm leading-relaxed">Send proof of payment to your consultant or email to support@fortuneoakwealth.com</li>
                    <li className="font-medium text-xs sm:text-sm leading-relaxed">Include your account ID in the payment reference/notes for faster processing</li>
                  </ul>
                </div>
              </div>
            </div>

            {selectedCrypto ? (
              <CryptoView 
                crypto={selectedCrypto} 
                onBack={() => setSelectedCrypto(null)} 
              />
            ) : (
              <>
                {/* Payment Method Selection */}
                {!selectedPaymentMethod ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {Object.keys(depositMethods).map((method) => (
                      <motion.button
                        key={method}
                        onClick={() => setSelectedPaymentMethod(method)}
                        className="p-4 rounded-xl border-2 card-glass bg-gradient-to-br from-slate-900/60 to-slate-800/60 border-slate-700 hover:border-blue-700 transition-all"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <h3 className="text-base sm:text-lg font-semibold mb-1 text-white">{depositMethods[method].title}</h3>
                        <p className="text-xs sm:text-sm text-blue-200">{depositMethods[method].description}</p>
                      </motion.button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <button
                      onClick={() => setSelectedPaymentMethod(null)}
                      className="mb-4 inline-flex items-center text-sm font-medium text-blue-300 hover:text-blue-400 transition-colors"
                    >
                      <ArrowLeftIcon className="w-5 h-5 mr-2" />
                      Back to Payment Methods
                    </button>

                    {selectedPaymentMethod === 'crypto' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {depositMethods.crypto.methods.map((crypto) => (
                          <motion.div
                            key={crypto.name}
                            className="card-glass bg-gradient-to-br from-slate-900/80 to-slate-800/80 p-4 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer border border-slate-700"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setSelectedCrypto(crypto)}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-semibold text-white">{crypto.name}</h4>
                              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                            <p className="text-sm text-blue-200">Click to view deposit details</p>
                          </motion.div>
                        ))}
                      </div>
                    )}

                    {selectedPaymentMethod === 'cashapp' && (
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                          <div className="w-full sm:w-32 h-32 card-glass bg-gradient-to-br from-green-700/80 to-green-800/80 rounded-xl flex items-center justify-center border border-green-700">
                            <span className="text-2xl sm:text-4xl font-bold text-green-300">
                              {depositMethods.cashapp.cashtag}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base sm:text-lg font-semibold mb-3 text-white">CashApp Instructions</h4>
                            <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base">
                              {depositMethods.cashapp.instructions.map((instruction, index) => (
                                <li key={index} className="text-blue-200">{instruction}</li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedPaymentMethod === 'paypal' && (
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                          <div className="w-full sm:w-32 h-32 card-glass bg-gradient-to-br from-blue-700/80 to-blue-800/80 rounded-xl flex items-center justify-center border border-blue-700">
                            <span className="text-xl sm:text-2xl font-bold text-blue-300 break-all px-2">
                              {depositMethods.paypal.email}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base sm:text-lg font-semibold mb-3 text-white">PayPal Instructions</h4>
                            <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base">
                              {depositMethods.paypal.instructions.map((instruction, index) => (
                                <li key={index} className="text-blue-200">{instruction}</li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedPaymentMethod === 'bank' && (
                      <div className="space-y-3">
                        <h4 className="text-base sm:text-lg font-semibold text-white">Bank Transfer Details</h4>
                        {Object.entries(depositMethods.bank.details).map(([key, value]) => (
                          <div key={key} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 card-glass bg-gradient-to-br from-slate-900/60 to-slate-800/60 rounded-xl border border-slate-700">
                            <span className="text-sm sm:text-base text-blue-200 capitalize mb-1 sm:mb-0">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <span className="text-sm sm:text-base font-medium text-white">{value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Processing Time Notice */}
            <div className="mt-6 p-4 card-glass bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-xl border border-slate-700">
              <h4 className="text-sm font-medium text-blue-200 mb-2">Processing Time</h4>
              <p className="text-sm text-blue-300">
                Deposits are typically processed within 1-2 business days.
                Contact support if you need assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const WithdrawPanel = () => {
    const [withdrawMethod, setWithdrawMethod] = useState('');
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [withdrawAddress, setWithdrawAddress] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [notifyByEmail, setNotifyByEmail] = useState(true);
    const [userEmail, setUserEmail] = useState(userData?.email || '');
    const [withdrawStatus, setWithdrawStatus] = useState(null);
    const [showOTP, setShowOTP] = useState(false);
    const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
    const [otpInput, setOtpInput] = useState('');
    const { verifyWithdrawalOTP, currentUser } = useAuth();
    const [bankDetails, setBankDetails] = useState({
      'Bank Name': '',
      'Account Number': '',
      'Routing Number': '',
      'Account Holder Name': '',
      'Bank Address': ''
    });

    // Calculate available balance for withdrawal
    const calculateAvailableBalance = () => {
      // Use the shared balance calculation
      const totalBalance = calculateTotalBalance();
      const pendingWithdrawals = userData?.accountDetails?.pendingWithdrawals || 0;
      
      // Calculate available balance (total balance minus pending withdrawals)
      const availableBalance = Math.max(0, totalBalance - parseFloat(pendingWithdrawals));
      return availableBalance;
    };

    const availableBalance = calculateAvailableBalance();
    
    // Check if user has active investments that haven't completed 21 days
    const hasActiveInvestment = () => {
      if (!userData?.investments || userData.investments.length === 0) return false;
      
      return userData.investments.some(investment => {
        if (investment.status === 'active') {
          const startDate = new Date(investment.startDate);
          const now = new Date();
          const elapsedDays = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
          return elapsedDays < 21; // Investment is still in progress
        }
        return false;
      });
    };

    const isWithdrawalBlocked = hasActiveInvestment();
  
    const handleWithdrawalSubmit = async (e) => {
      e.preventDefault();
      
      // Check if withdrawals are blocked due to active investment
      if (isWithdrawalBlocked) {
        alert("Withdrawals are temporarily disabled while your investment is active. Please wait until the 21-day period completes.");
        return;
      }
      
      // Validation
      if (!withdrawMethod) {
        alert("Please select a withdrawal method");
        return;
      }
      
      if (!withdrawAmount || parseFloat(withdrawAmount) < 100) {
        alert("Please enter a valid amount (minimum $100)");
        return;
      }

      const withdrawalAmount = parseFloat(withdrawAmount);
      
      // Check if withdrawal amount exceeds available balance
      if (withdrawalAmount > availableBalance) {
        alert(`Insufficient funds. Available balance: $${availableBalance.toLocaleString()}`);
        return;
      }
  
      // Store withdrawal details and show OTP verification
      setPendingWithdrawal({
        amount: withdrawalAmount,
        method: withdrawMethod,
        address: withdrawAddress,
        email: notifyByEmail ? userEmail : null,
        timestamp: new Date().toISOString(),
        ...(withdrawMethod === 'bank' ? { bankDetails } : {})
      });
      setShowOTP(true);
    };

    const handleOTPVerify = async (otp) => {
      try {
        setIsProcessing(true);
        await verifyWithdrawalOTP(currentUser.uid, otp);
        
        // Create withdrawal request in Firestore
        const withdrawalRequest = {
          ...pendingWithdrawal,
          userId: currentUser.uid,
          status: 'pending',
          userEmail: currentUser.email,
          timestamp: new Date().toISOString(),
          userDetails: {
            fullName: userData?.fullName || userData?.name || 'Unknown',
            accountLevel: userData?.accountLevel || 'standard',
            country: userData?.country || 'Unknown'
          },
          withdrawalDetails: {
            amount: parseFloat(withdrawAmount),
            method: withdrawMethod,
            address: withdrawAddress || null,
            currency: 'USD'
          },
          ...(withdrawMethod === 'bank' ? { bankDetails } : {})
        };

        // Add withdrawal request to Firestore
        const withdrawalRef = await addDoc(collection(db, 'withdrawalRequests'), withdrawalRequest);
        
        // Update user's pending withdrawals in a separate transaction
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          'accountDetails.pendingWithdrawals': increment(parseFloat(withdrawAmount))
        });

        // Add notification
        addWithdrawalNotification({
          amount: parseFloat(withdrawAmount),
          method: withdrawMethod
        });

        setWithdrawStatus({ 
          type: 'success', 
          message: 'Withdrawal request submitted successfully!'
        });
        
        // Reset form
        setWithdrawAmount('');
        setWithdrawMethod('');
        setWithdrawAddress('');
        setShowOTP(false);
        setPendingWithdrawal(null);
        setBankDetails({
          'Bank Name': '',
          'Account Number': '',
          'Routing Number': '',
          'Account Holder Name': '',
          'Bank Address': ''
        });
      } catch (err) {
        console.error('Withdrawal error:', err);
        setWithdrawStatus({ 
          type: 'error', 
          message: err.message || 'Failed to process withdrawal request'
        });
      } finally {
        setIsProcessing(false);
      }
    };

    const handleOTPCancel = () => {
      setShowOTP(false);
      setPendingWithdrawal(null);
      setWithdrawStatus(null);
      setOtpInput('');
    };
  
    const resetForm = () => {
      setWithdrawAmount('');
      setWithdrawAddress('');
      setWithdrawStatus(null);
      setShowOTP(false);
      setPendingWithdrawal(null);
      setOtpInput('');
      setBankDetails({
        'Bank Name': '',
        'Account Number': '',
        'Routing Number': '',
        'Account Holder Name': '',
        'Bank Address': ''
      });
    };
  
    return (
      <div className="p-6">
        <BackButton />
        <div className="card-glass bg-gradient-to-br from-slate-800/90 to-slate-900/90 rounded-2xl shadow-xl overflow-hidden border border-slate-700">
          <div className="border-b border-slate-700">
            <div className="p-4 bg-gradient-to-r from-blue-900/60 to-purple-900/60">
              <h2 className="text-lg font-semibold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Withdraw Funds</h2>
            </div>
          </div>
          <div className="p-6">
            {/* Withdrawal Method Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {Object.keys(withdrawMethods).map((method) => (
                <motion.button
                  key={method}
                  onClick={() => setWithdrawMethod(method)}
                  className={`p-4 rounded-xl border-2 card-glass bg-gradient-to-br from-slate-900/60 to-slate-800/60 transition-all ${
                    withdrawMethod === method 
                      ? 'border-blue-500 bg-blue-900/40' 
                      : 'border-slate-700 hover:border-blue-700'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isProcessing}
                >
                  <h3 className="text-lg font-semibold mb-2 text-white">{withdrawMethods[method].title}</h3>
                  <p className="text-sm text-blue-200">{withdrawMethods[method].description}</p>
                </motion.button>
              ))}
            </div>
            {/* Withdrawal Form and Status */}
            {withdrawMethod && (
              <div className="space-y-6">
                {/* Available Balance */}
                <div className={`card-glass p-4 rounded-xl border ${
                  isWithdrawalBlocked 
                    ? 'bg-gradient-to-br from-yellow-600/20 to-orange-600/20 border-yellow-500/30' 
                    : 'bg-gradient-to-br from-green-600/20 to-green-500/20 border-green-500/30'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${isWithdrawalBlocked ? 'text-yellow-200' : 'text-green-200'}`}>
                      {isWithdrawalBlocked ? 'Accumulated Returns:' : 'Available Balance:'}
                    </span>
                    <span className={`text-2xl font-bold ${isWithdrawalBlocked ? 'text-yellow-400' : 'text-green-400'}`}>
                      ${availableBalance.toLocaleString()}
                    </span>
                  </div>
                  {isWithdrawalBlocked && (
                    <div className="mt-2 p-3 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-yellow-200 font-medium">
                          Withdrawals blocked until investment period completes
                        </span>
                      </div>
                      <p className="text-xs text-yellow-300 mt-1">
                        Your returns are accumulating daily. Withdrawals will be available after 21 days.
                      </p>
                    </div>
                  )}
                </div>

                {/* Withdrawal Form */}
                <form onSubmit={handleWithdrawalSubmit} className="space-y-4">
                  {/* Amount Input */}
                  <div>
                    <label className="block text-sm font-medium text-blue-200 mb-2">
                      Withdrawal Amount (USD)
                    </label>
                    <input
                      type="number"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      className={`w-full border rounded-lg px-4 py-3 focus:outline-none text-white ${
                        isWithdrawalBlocked 
                          ? 'bg-slate-700 border-slate-600 cursor-not-allowed' 
                          : 'bg-slate-600 border-slate-500 focus:border-blue-400'
                      }`}
                      placeholder={isWithdrawalBlocked ? "Withdrawals temporarily disabled" : "Enter amount (minimum $100)"}
                      min="100"
                      step="0.01"
                      required
                      disabled={isWithdrawalBlocked}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Minimum: $100 | Fee: {withdrawMethods[withdrawMethod].fee}
                    </p>
                  </div>

                  {/* Enhanced Crypto Withdrawal Form */}
                  {withdrawMethod === 'crypto' && (
                    <div className="space-y-4">
                      {/* Crypto Selection */}
                      <div>
                        <label className="block text-sm font-medium text-blue-200 mb-2">
                          Select Cryptocurrency
                        </label>
                        <select
                          value={withdrawAddress}
                          onChange={(e) => setWithdrawAddress(e.target.value)}
                          className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white"
                          required
                        >
                          <option value="">Choose cryptocurrency</option>
                          <option value="bitcoin">Bitcoin (BTC)</option>
                          <option value="ethereum">Ethereum (ETH)</option>
                          <option value="usdt">USDT (TRC20)</option>
                          <option value="usdc">USDC (ERC20)</option>
                        </select>
                      </div>

                      {/* Wallet Address */}
                      <div>
                        <label className="block text-sm font-medium text-blue-200 mb-2">
                          Wallet Address
                        </label>
                        <input
                          type="text"
                          value={withdrawAddress}
                          onChange={(e) => setWithdrawAddress(e.target.value)}
                          className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white"
                          placeholder="Enter your wallet address"
                          required
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          Make sure to use the correct network (ERC20, TRC20, etc.)
                        </p>
                      </div>

                      {/* Crypto Information Card */}
                      <div className="card-glass bg-gradient-to-br from-blue-600/20 to-purple-600/20 p-4 rounded-xl border border-blue-500/30">
                        <h4 className="text-sm font-semibold text-blue-200 mb-2">Crypto Withdrawal Information</h4>
                        <div className="space-y-2 text-xs text-blue-300">
                          <div className="flex justify-between">
                            <span>Processing Time:</span>
                            <span className="font-medium">5-30 minutes</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Network Fee:</span>
                            <span className="font-medium">0.0005 - 0.01 (varies by network)</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Minimum Withdrawal:</span>
                            <span className="font-medium">$50</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Maximum Withdrawal:</span>
                            <span className="font-medium">$50,000 per day</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Enhanced CashApp Withdrawal Form */}
                  {withdrawMethod === 'cashapp' && (
                    <div className="space-y-4">
                      {/* CashApp Cashtag */}
                      <div>
                        <label className="block text-sm font-medium text-blue-200 mb-2">
                          Your CashApp Cashtag
                        </label>
                        <input
                          type="text"
                          value={withdrawAddress}
                          onChange={(e) => setWithdrawAddress(e.target.value)}
                          className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white"
                          placeholder="Enter your $cashtag (e.g., $johndoe)"
                          required
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          Include the $ symbol in your cashtag
                        </p>
                      </div>

                      {/* Full Name for CashApp */}
                      <div>
                        <label className="block text-sm font-medium text-blue-200 mb-2">
                          Full Name (as shown on CashApp)
                        </label>
                        <input
                          type="text"
                          className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white"
                          placeholder="Enter your full name"
                          required
                        />
                      </div>

                      {/* CashApp Information Card */}
                      <div className="card-glass bg-gradient-to-br from-green-600/20 to-green-500/20 p-4 rounded-xl border border-green-500/30">
                        <h4 className="text-sm font-semibold text-green-200 mb-2">CashApp Withdrawal Information</h4>
                        <div className="space-y-2 text-xs text-green-300">
                          <div className="flex justify-between">
                            <span>Processing Time:</span>
                            <span className="font-medium">Instant</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Fee:</span>
                            <span className="font-medium">1.5%</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Limit:</span>
                            <span className="font-medium">$7,500 per week</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Minimum Withdrawal:</span>
                            <span className="font-medium">$10</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Enhanced PayPal Withdrawal Form */}
                  {withdrawMethod === 'paypal' && (
                    <div className="space-y-4">
                      {/* PayPal Email */}
                      <div>
                        <label className="block text-sm font-medium text-blue-200 mb-2">
                          PayPal Email Address
                        </label>
                        <input
                          type="email"
                          value={withdrawAddress}
                          onChange={(e) => setWithdrawAddress(e.target.value)}
                          className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white"
                          placeholder="Enter your PayPal email"
                          required
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          Use the email associated with your PayPal account
                        </p>
                      </div>

                      {/* PayPal Account Name */}
                      <div>
                        <label className="block text-sm font-medium text-blue-200 mb-2">
                          PayPal Account Name
                        </label>
                        <input
                          type="text"
                          className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white"
                          placeholder="Enter your PayPal account name"
                          required
                        />
                      </div>

                      {/* Country Selection */}
                      <div>
                        <label className="block text-sm font-medium text-blue-200 mb-2">
                          Country
                        </label>
                        <select
                          className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white"
                          required
                        >
                          <option value="">Select your country</option>
                          <option value="US">United States</option>
                          <option value="CA">Canada</option>
                          <option value="UK">United Kingdom</option>
                          <option value="AU">Australia</option>
                          <option value="DE">Germany</option>
                          <option value="FR">France</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      {/* PayPal Information Card */}
                      <div className="card-glass bg-gradient-to-br from-blue-600/20 to-blue-500/20 p-4 rounded-xl border border-blue-500/30">
                        <h4 className="text-sm font-semibold text-blue-200 mb-2">PayPal Withdrawal Information</h4>
                        <div className="space-y-2 text-xs text-blue-300">
                          <div className="flex justify-between">
                            <span>Processing Time:</span>
                            <span className="font-medium">Instant</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Fee:</span>
                            <span className="font-medium">2.9% + $0.30</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Limit:</span>
                            <span className="font-medium">$10,000 per transaction</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Minimum Withdrawal:</span>
                            <span className="font-medium">$5</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bank Details for Bank Transfer */}
                  {withdrawMethod === 'bank' && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-blue-200">Bank Transfer Details Required:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {withdrawMethods[withdrawMethod].requirements.map((req) => (
                          <div key={req}>
                            <label className="block text-xs font-medium text-gray-400 mb-1">{req}</label>
                            <input
                              type="text"
                              className="w-full bg-slate-600 border border-slate-500 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 text-white text-sm"
                              placeholder={req}
                              required
                              value={bankDetails[req] || ''}
                              onChange={e => setBankDetails(prev => ({ ...prev, [req]: e.target.value }))}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Email Notification */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="notifyEmail"
                      checked={notifyByEmail}
                      onChange={(e) => setNotifyByEmail(e.target.checked)}
                      className="rounded border-slate-500 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="notifyEmail" className="text-sm text-blue-200">
                      Send withdrawal confirmation to my email
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isProcessing || isWithdrawalBlocked}
                    className={`w-full text-white py-3 rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isWithdrawalBlocked 
                        ? 'bg-gradient-to-r from-yellow-600 to-orange-600 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                    }`}
                  >
                    {isProcessing ? 'Processing...' : isWithdrawalBlocked ? 'Withdrawals Temporarily Disabled' : 'Submit Withdrawal Request'}
                  </button>
                </form>

                {/* Status Messages */}
                {withdrawStatus && (
                  <div className={`p-4 rounded-lg ${
                    withdrawStatus.type === 'success' 
                      ? 'bg-green-600/20 border border-green-500/30 text-green-200' 
                      : 'bg-red-600/20 border border-red-500/30 text-red-200'
                  }`}>
                    {withdrawStatus.message}
                  </div>
                )}

                {/* OTP Verification Modal */}
                {showOTP && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="card-glass bg-gradient-to-br from-slate-800/90 to-slate-900/90 p-6 rounded-2xl max-w-md w-full mx-4">
                      <h3 className="text-lg font-semibold text-white mb-4">Verify Withdrawal</h3>
                      <p className="text-blue-200 mb-4">
                        Please enter the verification code sent to your email to confirm this withdrawal.
                      </p>
                      <input
                        type="text"
                        value={otpInput}
                        onChange={(e) => setOtpInput(e.target.value)}
                        placeholder="Enter OTP"
                        className="w-full bg-slate-600 border border-slate-500 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-400 text-white mb-4"
                        maxLength="12"
                      />
                      <div className="flex space-x-3">
                        <button
                          onClick={handleOTPCancel}
                          className="flex-1 bg-slate-600 text-white py-2 rounded-lg hover:bg-slate-700 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleOTPVerify(otpInput)}
                          disabled={isProcessing || !otpInput}
                          className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50"
                        >
                          {isProcessing ? 'Verifying...' : 'Verify'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const DashboardPanel = () => {
    // Calculate days remaining for each active investment
    const activeInvestments = userData?.investments?.filter(inv => inv.status === 'active') || [];
    
    // Get the most recent active investment
    const latestInvestment = activeInvestments.length > 0 
      ? activeInvestments.reduce((latest, current) => {
          const currentDate = new Date(current.startDate);
          const latestDate = new Date(latest.startDate);
          return currentDate > latestDate ? current : latest;
        }, activeInvestments[0])
      : null;

    // Calculate totals from individual investments
    const calculateTotalInvested = () => {
      if (!userData?.investments || userData.investments.length === 0) return 0;
      return userData.investments.reduce((total, inv) => {
        return total + parseFloat(inv.amount || 0);
      }, 0);
    };

    const calculateTotalProfit = () => {
      if (!userData?.investments || userData.investments.length === 0) return 0;
      
      let totalProfit = 0;
      userData.investments.forEach(investment => {
        if (investment.status === 'active') {
          // Calculate profit for active investments
          const startDate = new Date(investment.startDate);
          const now = new Date();
          const elapsedDays = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
          const totalDays = 21;
          
          if (elapsedDays >= 0 && elapsedDays < totalDays) {
            const investmentAmount = parseFloat(investment.amount || 0);
            const multiplier = parseFloat(investment.multiplier || 10);
            const totalReturn = investmentAmount * multiplier;
            const dailyReturn = totalReturn / totalDays;
            const currentReturns = dailyReturn * elapsedDays;
            totalProfit += currentReturns - investmentAmount;
          } else if (elapsedDays >= totalDays) {
            const investmentAmount = parseFloat(investment.amount || 0);
            const multiplier = parseFloat(investment.multiplier || 10);
            totalProfit += (investmentAmount * multiplier) - investmentAmount;
          }
        } else {
          // For completed investments, calculate profit from balance
          const investmentAmount = parseFloat(investment.amount || 0);
          const balance = parseFloat(investment.balance || 0);
          totalProfit += balance - investmentAmount;
        }
      });
      
      return totalProfit;
    };

    // Calculate total balance using the shared function (defined in main component scope)

    // Calculate days remaining and progress for the latest investment
    const getDaysRemaining = (investment) => {
      if (!investment) return 0;
      const startDate = new Date(investment.startDate);
      const endDate = new Date(investment.endDate);
      const now = new Date();
      const totalDays = 21;
      const elapsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
      return Math.max(0, totalDays - elapsed);
    };

    const getProgress = (investment) => {
      if (!investment) return 0;
      const totalDays = 21;
      const remaining = getDaysRemaining(investment);
      return ((totalDays - remaining) / totalDays) * 100;
    };

    // Calculate daily return rate for active investments
    const getDailyReturnRate = (investment) => {
      if (!investment || investment.status !== 'active') return 0;
      const investmentAmount = parseFloat(investment.amount || 0);
      const multiplier = parseFloat(investment.multiplier || 10);
      const totalReturn = investmentAmount * multiplier;
      const dailyReturn = totalReturn / 21; // 21 days
      return dailyReturn;
    };

    // Calculate current accumulated returns for active investment
    const getCurrentReturns = (investment) => {
      if (!investment || investment.status !== 'active') return 0;
      const startDate = new Date(investment.startDate);
      const now = new Date();
      const elapsedDays = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
      const dailyReturn = getDailyReturnRate(investment);
      return dailyReturn * elapsedDays;
    };

    return (
      <div className="space-y-8 pb-8">
        {/* Welcome Card with User Name */}
        <div className="card-glass bg-gradient-to-r from-blue-700/80 to-purple-700/80 rounded-2xl shadow-xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Welcome back, {userData?.fullName || userData?.name || 'Investor'}! <span className="text-white">👋</span>
              </h1>
              <p className="text-blue-100">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-blue-100">Account Balance</p>
                <p className="text-xl font-bold">${calculateTotalBalance().toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <UserCircleIcon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Active Investment Plan - moved to the very top */}
        {latestInvestment ? (
          <button
            className="w-full text-left card-glass bg-gradient-to-r from-primary-700/80 to-primary-900/80 rounded-2xl shadow-xl p-8 focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer hover:brightness-105 hover:shadow-2xl transition-all relative"
            onClick={() => setShowInvestmentsModal(true)}
            title="Click to view all investments"
          >
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-white mb-4 md:mb-0">
                <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  {latestInvestment.planName}
                </h2>
                <p className="text-white/80">Investment Amount: ${parseFloat(latestInvestment.amount).toLocaleString()}</p>
                <p className="text-white/80">Expected Return: ${parseFloat(latestInvestment.expectedReturn).toLocaleString()}</p>
                <p className="text-green-400 font-medium">
                  Daily Return: ${getDailyReturnRate(latestInvestment).toLocaleString()}
                </p>
                <p className="text-yellow-400 font-medium">
                  Accumulated: ${getCurrentReturns(latestInvestment).toLocaleString()}
                </p>
              </div>
              <div className="flex flex-col items-center">
                <div className="bg-white/10 rounded-lg p-4 text-center min-w-[120px] mb-3">
                  <span className="text-3xl font-bold text-white">{getDaysRemaining(latestInvestment)}</span>
                  <p className="text-sm text-white/80 mt-1">Days Remaining</p>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2.5">
                  <div 
                    className="bg-green-400 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${getProgress(latestInvestment)}%` }}
                  />
                </div>
                <p className="text-sm text-white/80 mt-2">
                  {Math.round(getProgress(latestInvestment))}% Complete
                </p>
              </div>
            </div>
          </button>
        ) : (
          <div className="card-glass bg-gradient-to-r from-slate-800/80 to-slate-900/80 rounded-2xl shadow-xl p-8 text-center">
            <h2 className="text-xl font-semibold text-white mb-2">No Active Investment Plan</h2>
            <p className="text-gray-300 mb-4">Start your investment journey today!</p>
            <button
              onClick={() => window.open('/investment-plans', '_blank')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-xl shadow hover:brightness-110 transition-colors font-semibold"
            >
              View Investment Plans
            </button>
          </div>
        )}

        {/* Investments Modal */}
        <Transition.Root show={showInvestmentsModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={setShowInvestmentsModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
              leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" />
            </Transition.Child>
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl card-glass bg-gradient-to-br from-slate-800/90 to-slate-900/90 p-6 text-left align-middle shadow-2xl transition-all">
                    <Dialog.Title as="h3" className="text-lg font-bold leading-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4 flex justify-between items-center">
                      All Investments
                      <button onClick={() => setShowInvestmentsModal(false)} className="text-gray-400 hover:text-primary-600 text-2xl font-bold">&times;</button>
                    </Dialog.Title>
                    <div className="divide-y divide-slate-700/60">
                      {userData?.investments && userData.investments.length > 0 ? (
                        userData.investments.map((inv, idx) => (
                          <div key={inv.id || idx} className="py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                              <div className="font-semibold text-blue-300">{inv.planName}</div>
                              <div className="text-sm text-gray-400">{new Date(inv.startDate).toLocaleDateString()} - {new Date(inv.endDate).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-500">Status: <span className={inv.status === 'active' ? 'text-green-400' : 'text-gray-400'}>{inv.status}</span></div>
                            </div>
                            <div className="flex flex-col md:items-end">
                              <div className="text-sm text-gray-300">Amount: <span className="font-medium text-white">${parseFloat(inv.amount).toLocaleString()}</span></div>
                              <div className="text-sm text-gray-300">Expected Return: <span className="font-medium text-white">${parseFloat(inv.expectedReturn).toLocaleString()}</span></div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-8 text-center text-gray-500">No investments found.</div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Collapsible Original Stat Cards */}
        <div className="mb-8">
          <button
            className="mb-4 flex items-center space-x-3 text-blue-300 font-semibold focus:outline-none card-glass bg-gradient-to-r from-slate-800/80 to-slate-900/80 rounded-xl px-4 py-3 shadow border border-slate-700 hover:shadow-lg transition-all duration-200 w-full justify-between"
            onClick={() => setShowStatCards((prev) => !prev)}
            aria-expanded={showStatCards}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                showStatCards ? 'bg-blue-700 text-blue-200' : 'bg-slate-700 text-slate-300'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <span className="text-lg">{showStatCards ? 'Hide Summary' : 'Show Summary'}</span>
              <span className="text-sm text-gray-400 font-normal">
                {showStatCards ? 'Click to collapse' : 'Click to expand'}
              </span>
            </div>
            <svg className={`w-5 h-5 transition-transform duration-200 ${showStatCards ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showStatCards && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <motion.div
                  className="card-glass bg-gradient-to-br from-emerald-500/80 to-emerald-700/80 rounded-2xl shadow-xl p-6"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white/90 font-medium">Total Balance</h3>
                    <BanknotesIcon className="h-6 w-6 text-white/80" />
                  </div>
                  <p className="text-3xl font-bold text-white">
                    ${calculateTotalBalance().toLocaleString()}
                  </p>
                  <p className="text-sm text-white/80 mt-2">Current balance</p>
                </motion.div>

                <motion.div
                  className="card-glass bg-gradient-to-br from-indigo-500/80 to-indigo-700/80 rounded-2xl shadow-xl p-6"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white/90 font-medium">Total Invested</h3>
                    <ChartBarIcon className="h-6 w-6 text-white/80" />
                  </div>
                  <p className="text-3xl font-bold text-white">
                    ${calculateTotalInvested().toLocaleString()}
                  </p>
                  <p className="text-sm text-white/80 mt-2">Current investments</p>
                </motion.div>

                <motion.div
                  className="card-glass bg-gradient-to-br from-rose-500/80 to-rose-700/80 rounded-2xl shadow-xl p-6"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white/90 font-medium">Total Profit</h3>
                    <ArrowTrendingUpIcon className="h-6 w-6 text-white/80" />
                  </div>
                  <p className="text-3xl font-bold text-white">
                    ${calculateTotalProfit().toLocaleString()}
                  </p>
                  <p className="text-sm text-white/80 mt-2">Expected earnings</p>
                </motion.div>

                <motion.div
                  className="card-glass bg-gradient-to-br from-cyan-500/80 to-cyan-700/80 rounded-2xl shadow-xl p-6"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white/90 font-medium">Active Plans</h3>
                    <ShieldCheckIcon className="h-6 w-6 text-white/80" />
                  </div>
                  <p className="text-3xl font-bold text-white">
                    {activeInvestments.length}
                  </p>
                  <p className="text-sm text-white/80 mt-2">Currently running</p>
                </motion.div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Per-Investment Stat Cards - now directly below summary cards */}
        {userData?.investments && userData.investments.length > 0 && (
          <div className="space-y-8 mb-8">
            {userData.investments.map((inv, idx) => (
              <div key={inv.id || idx} className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-primary-800 mb-1">{inv.planName}</h2>
                    <div className="text-sm text-gray-500">{new Date(inv.startDate).toLocaleDateString()} - {new Date(inv.endDate).toLocaleDateString()}</div>
                    <div className="text-xs text-gray-400 mt-1">Status: <span className={inv.status === 'active' ? 'text-green-600' : 'text-gray-600'}>{inv.status}</span></div>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <span className="inline-block bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-xs font-semibold">Investment ID: {inv.id || idx + 1}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <StatCard
                    title="Invested Amount"
                    value={`$${parseFloat(inv.amount).toLocaleString()}`}
                    color="from-blue-500 to-blue-600"
                    subtext="Principal"
                    icon={<ChartBarIcon className="h-6 w-6 text-white/80" />}
                  />
                  <StatCard
                    title="Profit"
                    value={`$${parseFloat(inv.profit || inv.expectedReturn || 0).toLocaleString()}`}
                    color="from-purple-500 to-purple-600"
                    subtext="Expected earnings"
                    icon={<ArrowTrendingUpIcon className="h-6 w-6 text-white/80" />}
                  />
                  <StatCard
                    title="Balance"
                    value={`$${parseFloat(inv.balance || 0).toLocaleString()}`}
                    color="from-green-500 to-green-600"
                    subtext="Current balance"
                    icon={<BanknotesIcon className="h-6 w-6 text-white/80" />}
                  />
                  <StatCard
                    title="Status"
                    value={inv.status === 'active' ? 'Active' : 'Completed'}
                    color="from-amber-500 to-amber-600"
                    subtext={inv.status === 'active' ? 'Running plan' : 'Finished'}
                    icon={<CircleStackIcon className="h-6 w-6 text-white/80" />}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* The rest of the dashboard content follows */}
        {/* Recent Activity */}
        <div className="card-glass bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl shadow-xl p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-blue-300">Recent Activity</h2>
            <button className="text-blue-400 text-sm font-medium hover:text-blue-300">View All</button>
          </div>
          <div className="space-y-3">
            {generateRecentActivity().map((activity, index) => (
              <motion.div
                key={activity.id}
                className="flex items-center justify-between p-3 card-glass bg-gradient-to-r from-slate-900/60 to-slate-800/60 rounded-xl border border-slate-700"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-2 rounded-full ${
                    activity.type === 'investment' ? 'bg-blue-600/20' :
                    activity.type === 'returns' ? 'bg-green-600/20' :
                    activity.type === 'completion' ? 'bg-purple-600/20' :
                    activity.type === 'withdrawal' ? 'bg-red-600/20' :
                    activity.type === 'account' ? 'bg-cyan-600/20' : 'bg-slate-600/20'
                  }`}> 
                    {activity.icon === 'investment' && (
                      <ArrowUpCircleIcon className="h-6 w-6 text-blue-400" />
                    )}
                    {activity.icon === 'returns' && (
                      <ArrowTrendingUpIcon className="h-6 w-6 text-green-400" />
                    )}
                    {activity.icon === 'completion' && (
                      <CheckCircleIcon className="h-6 w-6 text-purple-400" />
                    )}
                    {activity.icon === 'withdrawal' && (
                      <ArrowDownCircleIcon className="h-6 w-6 text-red-400" />
                    )}
                    {activity.icon === 'account' && (
                      <UserCircleIcon className="h-6 w-6 text-cyan-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white">
                      {activity.title}
                    </p>
                    <p className="text-sm text-blue-300">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(activity.timestamp).toLocaleDateString()} at {new Date(activity.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {activity.amount > 0 && (
                    <p className={`font-semibold ${
                      activity.type === 'returns' ? 'text-green-400' :
                      activity.type === 'withdrawal' ? 'text-red-400' :
                      activity.type === 'investment' ? 'text-blue-400' :
                      activity.type === 'completion' ? 'text-purple-400' : 'text-white'
                    }`}>
                      ${activity.amount.toLocaleString()}
                    </p>
                  )}
                  <p className={`text-sm ${
                    activity.status === 'completed' ? 'text-green-400' :
                    activity.status === 'active' ? 'text-blue-400' :
                    activity.status === 'pending' ? 'text-yellow-400' : 'text-gray-400'
                  }`}>
                    {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                  </p>
                </div>
              </motion.div>
            ))}
            {generateRecentActivity().length === 0 && (
              <div className="text-center text-blue-300 py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-700/50 rounded-full flex items-center justify-center">
                  <ClockIcon className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-lg font-medium">No recent activity</p>
                <p className="text-sm text-slate-400 mt-1">Start investing to see your activity here</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <motion.button
            onClick={() => setActiveTab('deposit')}
            className="card-glass bg-gradient-to-br from-green-700/80 to-emerald-700/80 p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow flex flex-col items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-green-600/30 p-3 rounded-full mb-3">
              <ArrowUpCircleIcon className="h-6 w-6 text-green-300" />
            </div>
            <h3 className="text-sm font-semibold text-white">Deposit</h3>
          </motion.button>

          <motion.button
            onClick={() => setActiveTab('withdraw')}
            className="card-glass bg-gradient-to-br from-red-700/80 to-pink-700/80 p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow flex flex-col items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-red-600/30 p-3 rounded-full mb-3">
              <ArrowDownCircleIcon className="h-6 w-6 text-red-300" />
            </div>
            <h3 className="text-sm font-semibold text-white">Withdraw</h3>
          </motion.button>

          <motion.button
            onClick={() => window.open('/investment-plans', '_blank')}
            className="card-glass bg-gradient-to-br from-blue-700/80 to-purple-700/80 p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow flex flex-col items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-blue-600/30 p-3 rounded-full mb-3">
              <ChartBarIcon className="h-6 w-6 text-blue-300" />
            </div>
            <h3 className="text-sm font-semibold text-white">Invest</h3>
          </motion.button>

          <motion.button
            onClick={() => setActiveTab('notifications')}
            className="card-glass bg-gradient-to-br from-cyan-700/80 to-cyan-900/80 p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow flex flex-col items-center relative"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-cyan-600/30 p-3 rounded-full mb-3">
              <BellIcon className="h-6 w-6 text-cyan-300" />
            </div>
            <h3 className="text-sm font-semibold text-white">Notifications</h3>
            {notifications.filter(n => !n.read).length > 0 && (
              <motion.span 
                className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              >
                {notifications.filter(n => !n.read).length}
              </motion.span>
            )}
          </motion.button>
        </div>

        {/* Portfolio Performance */}
        <div className="card-glass bg-gradient-to-br from-blue-800/80 to-purple-900/80 rounded-2xl shadow-xl p-8">
          <h2 className="text-lg font-semibold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Portfolio Performance</h2>
          <PortfolioPerformance 
            portfolioData={[
              { name: 'Total Balance', value: Math.round((calculateTotalBalance() / (calculateTotalInvested() || 1)) * 100) },
              { name: 'Total Invested', value: Math.round((calculateTotalInvested() / (calculateTotalInvested() || 1)) * 100) },
              { name: 'Total Profit', value: Math.round((calculateTotalProfit() / (calculateTotalInvested() || 1)) * 100) },
              { name: 'Active Plans', value: Math.round((userData?.investments?.filter(inv => inv.status === 'active').length || 0) / (userData?.investments?.length || 1) * 100) }
            ]}
            performanceData={userData?.investments?.map((inv, index) => ({
              month: new Date(inv.startDate).toISOString().split('T')[0],
              value: parseFloat(inv.amount || 0),
              profit: parseFloat(inv.profit || inv.expectedReturn || 0)
            })) || []}
            userData={{
              balance: calculateTotalBalance(),
              totalInvested: calculateTotalInvested(),
              totalProfit: calculateTotalProfit(),
              investments: userData?.investments || []
            }}
          />
        </div>
      </div>
    );
  };

  if (loading || error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        {loading ? (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        ) : (
          <div className="text-red-600 text-center p-4">
            <p className="text-xl font-semibold">Error loading dashboard</p>
            <p className="mt-2">{error}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <MobileNav />
        <Sidebar />
        <div className="lg:ml-64 relative z-10">
        <div className="pt-20 lg:pt-4 px-4 lg:p-8">
            <div className="max-w-7xl mx-auto">
              {activeTab === 'dashboard' && <DashboardPanel />}
              {activeTab === 'deposit' && <DepositPanel />}
              {activeTab === 'withdraw' && <WithdrawPanel />}
              {activeTab === 'notifications' && <NotificationsPanel />}
          </div>
        </div>
      </div>
    </div>
  );
}

// Updated StatCard Component to handle subtext and icon
const StatCard = ({ title, value, color, subtext, icon }) => (
  <div className={`bg-gradient-to-r ${color} rounded-lg shadow-lg p-3 lg:p-6 text-white`}>
    <div className="flex items-center justify-between">
      <h3 className="text-xs lg:text-sm text-white/80">{title}</h3>
      {icon && <div>{icon}</div>}
    </div>
    <p className="text-lg lg:text-2xl font-bold mt-1 lg:mt-2">{value}</p>
    {subtext && (
      <p className="text-xs lg:text-sm mt-1 lg:mt-2 text-white/80">
        {subtext}
      </p>
    )}
  </div>
);

export default UserDashboard; 